/**
 * DatacenterIpDatabaseAwsGcpAzureDoLinodeOvhPage — CRUD list for `datacenter-ip-database-aws-gcp-azure-do-linode-ovh`.
 * Header + create form + table of items with delete action.
 */
import { useState, type FormEvent } from "react";
import {
	useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhList,
	useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhCreate,
	useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhDelete,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

export function DatacenterIpDatabaseAwsGcpAzureDoLinodeOvhPage() {
	const listQuery = useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhList();
	const createMut = useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhCreate();
	const deleteMut = useDatacenterIpDatabaseAwsGcpAzureDoLinodeOvhDelete();

	const [name, setName] = useState("");

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;
		createMut.mutate({ name: name.trim() }, {
			onSuccess: () => setName(""),
		});
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					datacenter-ip-database-aws-gcp-azure-do-linode-ovh
				</p>
				<h1 className="text-3xl text-green-700 font-semibold">
					DatacenterIpDatabaseAwsGcpAzureDoLinodeOvh
				</h1>
			</header>

			<section className={CARD} aria-label="Create datacenter-ip-database-aws-gcp-azure-do-linode-ovh">
				<h2 className="text-base font-semibold text-zinc-900">Create new</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Name"
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-name-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !name.trim()}
						data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-submit"
					>
						{createMut.isPending ? "Saving." : "Create"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="DatacenterIpDatabaseAwsGcpAzureDoLinodeOvh list">
				<h2 className="text-base font-semibold text-zinc-900">Items</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-empty"
					>
						No items yet.
					</p>
				) : (
					<ul className="mt-3 divide-y divide-zinc-200" data-testid="datacenter-ip-database-aws-gcp-azure-do-linode-ovh-list">
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between py-2 text-sm"
								data-testid={`datacenter-ip-database-aws-gcp-azure-do-linode-ovh-row-${row.id}`}
							>
								<span className="font-mono text-xs text-zinc-600">
									{row.id}
								</span>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(row.id)}
									disabled={deleteMut.isPending}
									data-testid={`datacenter-ip-database-aws-gcp-azure-do-linode-ovh-delete-${row.id}`}
								>
									Delete
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default DatacenterIpDatabaseAwsGcpAzureDoLinodeOvhPage;
